import isEqual from 'lodash-es/isEqual';
import { isObservableArray } from 'mobx';
import preserveAttendeeData from 'owa-calendar-compose-form-store/lib/utils/preserveAttendeeData';
import areAttendeeTypeCollectionsEqual from 'owa-calendar-forms-common/lib/utils/areAttendeeTypeCollectionsEqual';
import calendarEventPropertyKeys from './calendarEventPropertyKeys';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import areEnhancedLocationsEqual from 'owa-location-persona/lib/utils/areEnhancedLocationsEqual';

export default function generateEventDiff(
    event: CalendarEvent,
    originalEvent: CalendarEvent
): CalendarEvent {
    let diffEvent: CalendarEvent = {} as CalendarEvent;

    Object.keys(originalEvent).forEach(property => {
        if (originalEvent.hasOwnProperty(property)) {
            // isEquals does not work when comparing two mobx observable arrays so they must be spliced so they are of a normal array type
            let areValuesEqual: boolean = false;
            if (isObservableArray(originalEvent[property]) || isObservableArray(event[property])) {
                areValuesEqual =
                    originalEvent[property] != null &&
                    event[property] != null &&
                    isEqual(originalEvent[property].slice(), event[property].slice());
            } else {
                areValuesEqual = isEqual(originalEvent[property], event[property]);
            }

            if (!areValuesEqual) {
                switch (property) {
                    /**
                     * While updating times or time zone, update API expects both values.
                     * If time zone is not present, server will assume it is the default.
                     */
                    case calendarEventPropertyKeys.Start:
                    case calendarEventPropertyKeys.StartTimeZoneId:
                        diffEvent.Start = event.Start;
                        diffEvent.StartTimeZoneId = event.StartTimeZoneId;
                        break;
                    case calendarEventPropertyKeys.End:
                    case calendarEventPropertyKeys.EndTimeZoneId:
                        diffEvent.End = event.End;
                        diffEvent.EndTimeZoneId = event.EndTimeZoneId;
                        break;
                    /**
                     * Currently, since the location well uses the LocationPersonaControlViewState to store location information only the displayName and postalAddress
                     * will be changed on the data location objects because that is the only information returned. This will not be needed when locationWell is modified to
                     * use enhanced locations
                     */
                    case calendarEventPropertyKeys.Locations:
                        if (
                            areEnhancedLocationsEqual(event.Locations, originalEvent.Locations) ==
                            false
                        ) {
                            diffEvent[property] = event[property];
                        }
                        break;
                    /**
                     * While updating optional or required attendees, update API expects both required and optional attendees.
                     * If one proprerty is not sent, then that property is updated as empty list by API.
                     */
                    case calendarEventPropertyKeys.RequiredAttendees:
                    case calendarEventPropertyKeys.OptionalAttendees:
                    case calendarEventPropertyKeys.Resources:
                        if (
                            property === calendarEventPropertyKeys.Resources &&
                            areAttendeeTypeCollectionsEqual(
                                event.Resources,
                                originalEvent.Resources
                            )
                        ) {
                            break;
                        }
                        /** It's possible the original event has data we want to preserve for attendees on the edited event.
                         * For example, if an attendee were removed and then added back, we'll only have Mailbox data (VSO 28014).
                         * Instead of overwriting the existing set, we should preserve data on attendees that exist in both sets.
                         */
                        diffEvent.RequiredAttendees = preserveAttendeeData(
                            originalEvent.RequiredAttendees,
                            event.RequiredAttendees
                        );
                        diffEvent.OptionalAttendees = preserveAttendeeData(
                            originalEvent.OptionalAttendees,
                            event.OptionalAttendees
                        );
                        diffEvent.Resources = event.Resources;
                        break;
                    case calendarEventPropertyKeys.ReminderMinutesBeforeStart:
                        diffEvent.ReminderIsSet = event.ReminderIsSet;

                        // -1 is invalid for Desktop Outlook, so don't write that.
                        if (event.ReminderMinutesBeforeStart != -1) {
                            diffEvent.ReminderMinutesBeforeStart = event.ReminderMinutesBeforeStart;
                        }
                        break;
                    case calendarEventPropertyKeys.Subject:
                        // Handles the case for a new event when the user types and then deletes the subject.
                        // Consider null and empty string to be equal
                        areValuesEqual = !originalEvent[property] && !event[property];
                        if (!areValuesEqual) {
                            diffEvent[property] = event[property];
                        }
                        break;

                    default:
                        diffEvent[property] = event[property];
                }
            }
        }

        /**
         * IsOnlineMeeting and OnlineMeetingProvider properties are not meant to be sent when the event is a draft
         * else they will be wiped by the server when the updateCalendarEvent call is made. Incident 1748647.
         */
        switch (property) {
            case calendarEventPropertyKeys.IsOnlineMeeting:
                diffEvent.IsOnlineMeeting = event.IsDraft ? undefined : event.IsOnlineMeeting;
                break;
            case calendarEventPropertyKeys.OnlineMeetingProvider:
                diffEvent.OnlineMeetingProvider = event.IsDraft
                    ? undefined
                    : event.OnlineMeetingProvider;
                break;
        }
    });

    return diffEvent;
}
